/**
 * Every hydration mismatch Vue reports starts its warning with this word, and
 * nothing else Vue warns about in a production build does.
 */
const DETAILED_PREFIX = 'Hydration '

/** What Vue puts in front of a warning on its way to the console. */
const VUE_WARN_PREFIX = '[Vue warn]: '

/**
 * The one line Vue logs through `console.error` at the end of a hydration that
 * found anything, whether or not the build compiled the detailed warnings in.
 */
const SUMMARY = 'Hydration completed but contains mismatches'

/**
 * How much of the first warning travels with the report: its category only,
 * the kind of mismatch and the element, up to the first colon. The rendered
 * and expected values after it are whatever the page drew, which on a rendered
 * home page can be visitor specific, so they never leave the browser.
 */
const SAMPLE_LENGTH = 200

/** The category of a Vue hydration warning, without the values it compared. */
export function hydrationMismatchCategory(text: string): string {
  return text.split(/[:\n]/, 1)[0].trim().slice(0, SAMPLE_LENGTH)
}

export interface HydrationMismatchCounter {
  /** A line Vue wrote to console.warn. True when it named a mismatch. */
  recordWarning: (message: unknown) => boolean
  /** Vue's end of hydration error. True when it was the mismatch summary. */
  recordSummary: (message: unknown) => boolean
  /** Mismatches on this page view. */
  total: () => number
  /** The category of the first mismatch Vue named, for the report. */
  sample: () => string | undefined
}

/**
 * Counts the hydration mismatches of one page view.
 *
 * A build with `__VUE_PROD_HYDRATION_MISMATCH_DETAILS__` on warns once per
 * mismatch, which is the real count. A build without it only logs the summary
 * line, and then all the counter can honestly say is one.
 */
export function createHydrationMismatchCounter(): HydrationMismatchCounter {
  let detailed = 0
  let summary = false
  let first: string | undefined

  return {
    recordWarning(message: unknown): boolean {
      if (typeof message !== 'string') return false
      const text = message.startsWith(VUE_WARN_PREFIX)
        ? message.slice(VUE_WARN_PREFIX.length)
        : message
      if (!text.startsWith(DETAILED_PREFIX)) return false
      detailed += 1
      first ??= hydrationMismatchCategory(text)
      return true
    },

    recordSummary(message: unknown): boolean {
      if (typeof message !== 'string') return false
      if (!message.includes(SUMMARY)) return false
      summary = true
      return true
    },

    total(): number {
      if (detailed > 0) return detailed
      return summary ? 1 : 0
    },

    sample(): string | undefined {
      return first
    },
  }
}

/** How many ancestors of the mismatched element the location names. */
const LOCATION_DEPTH = 5

/** Classes per element in the location, enough to find the component. */
const LOCATION_CLASSES = 3

/** Longest a location may be, the endpoint refuses anything longer. */
export const LOCATION_LENGTH = 300

/** A class or test id is code the page was built from, never visitor text. */
const SAFE_TOKEN = /^[A-Za-z0-9_-]{1,40}$/

/**
 * A class carrying an id the page was given at runtime, like the video
 * player's `s2nPlayer-<article id>`: a long run of digits or of hex.
 */
const RUNTIME_VALUE = /\d{4,}|[0-9a-f]{8,}/i

const isStaticToken = (name: string) =>
  SAFE_TOKEN.test(name) && !RUNTIME_VALUE.test(name)

const isNode = (value: unknown): value is Node =>
  typeof Node !== 'undefined' && value instanceof Node

/** One element as `tag[data-testid].class.class`, from code only. */
function describeElement(element: Element): string {
  const tag = element.tagName.toLowerCase()
  const testId = element.getAttribute('data-testid')
  const classes = [...element.classList]
    .filter(isStaticToken)
    .slice(0, LOCATION_CLASSES)
  return [
    tag,
    testId && isStaticToken(testId) ? `[${testId}]` : '',
    ...classes.map((name) => `.${name}`),
  ].join('')
}

/**
 * Where on the page a hydration mismatch happened, from the arguments Vue
 * passed to console.warn: the node the server rendered, its nearest ancestors,
 * and the element the browser expected in its place. Built from tag names,
 * test ids and classes only. Text, attribute values and anything else the page
 * drew stay in the browser, the same rule the sample follows.
 *
 * For example `#comment in div.flex.items-center < nav[navbar] < body, expected <button>`.
 */
export function hydrationMismatchLocation(args: unknown[]): string | undefined {
  const node = args.find(isNode)
  if (!node) return undefined
  const start = node instanceof Element ? node : node.parentElement
  const path: string[] = []
  for (
    let element = start;
    element && path.length < LOCATION_DEPTH;
    element = element.parentElement
  ) {
    path.push(describeElement(element))
  }
  const rendered = node instanceof Element ? '' : `${node.nodeName} in `
  const expectedIndex = args.findIndex(
    (arg) => typeof arg === 'string' && /expected on client:\s*$/.test(arg)
  )
  const expected = expectedIndex >= 0 ? args[expectedIndex + 1] : undefined
  const expectedTag =
    typeof expected === 'string' && /^[a-z][a-z0-9-]*$/.test(expected)
      ? `, expected <${expected}>`
      : ''
  // Drop whole ancestors from the far end rather than cut one in half, the
  // endpoint refuses a location that does not parse.
  let location = `${rendered}${path.join(' < ')}`
  while (path.length > 1 && location.length > LOCATION_LENGTH) {
    path.pop()
    location = `${rendered}${path.join(' < ')}`
  }
  if (location.length > LOCATION_LENGTH) return undefined
  return location.length + expectedTag.length <= LOCATION_LENGTH
    ? `${location}${expectedTag}`
    : location
}
